// v84.34 — Shared CreateWODialog. Dipakai di /work-orders (list) DAN klik slot
// kosong di kalender dashboard. Form yg ringan/responsif (versi lama sebelum
// v84.32 refactor) + color picker event + auto GCal sync setelah save.
//
// Estimasi durasi (scheduled_end) dihitung otomatis di backend berdasarkan
// service_type + total quantity. Frontend tidak perlu compute.
import { useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { dispatchWOChanged } from "@/lib/woEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Wand2, Palette, X, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { svcLabel } from "@/lib/serviceLabel";
import { SherlockManualDialog } from "@/components/SherlockManualDialog";
import { WOTechEvidence } from "@/components/WOTechEvidence";

const SERVICES = ["Service", "Perbaikan", "Pasang", "Bongkar-Pasang", "Cek", "Garansi", "Survey"];
const SERVICE_ADD_KEY = "__CUSTOM__";

// v84.148 — Normalisasi service_type case-insensitive supaya Select dropdown
// bisa match walau WO backend simpan UPPER-case ("SERVICE", "GARANSI", "PASANG")
// atau format lain ("BONGKAR_PASANG" vs "Bongkar-Pasang"). Kalau tidak match
// SERVICES canonical, kembalikan value asli — di render akan diberi fallback
// SelectItem supaya tetap tampil.
function normalizeServiceType(v) {
  if (!v) return "";
  const raw = String(v).trim();
  const key = raw.toLowerCase().replace(/[_\s-]+/g, "");
  const found = SERVICES.find(s => s.toLowerCase().replace(/[_\s-]+/g, "") === key);
  return found || raw;
}

// v84.34 — Palette 12 warna Google Calendar-compatible + opsi Otomatis
const GOOGLE_CAL_COLORS = [
  { hex: "#616161", name: "Grafit" },   /* v84.37 — Grafit menggantikan Hitam supaya tidak blend dgn dark bg */
  { hex: "#7986CB", name: "Lavender" },
  { hex: "#33B679", name: "Sage" },
  { hex: "#8E24AA", name: "Grape" },
  { hex: "#E67C73", name: "Flamingo" },
  { hex: "#F6BF26", name: "Banana" },
  { hex: "#F4511E", name: "Tangerine" },
  { hex: "#039BE5", name: "Peacock" },
  { hex: "#3F51B5", name: "Blueberry" },
  { hex: "#0B8043", name: "Basil" },
  { hex: "#D50000", name: "Tomato" },
];

function todayWita() {
  return new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function makeEmptyForm(overrides = {}) {
  return {
    // v84.30 — items array multi-layanan (default 1 baris Service·1 unit)
    items: [{ service_type: "Service", quantity: 1 }],
    custom_service: "",
    remarks: "", address: "", customer_phone: "", customer_name: "",
    preferred_date: overrides.day || todayWita(),
    preferred_time: overrides.hour != null ? `${String(overrides.hour).padStart(2, "0")}:00` : "09:00",
    maps_link: "", description: "", technician_id: overrides.technician_id || "",
    color: null,   // null = otomatis (warna teknisi / AI)
  };
}

function makeFormFromWO(wo) {
  // v84.40 — Konversi WO backend → shape form CreateWODialog utk edit mode
  // v84.148 — Normalisasi service_type case-insensitive supaya Select match
  const items = Array.isArray(wo.items) && wo.items.length
    ? wo.items.map(it => ({
        service_type: normalizeServiceType(it.service_type) || "OTHER",
        quantity: it.quantity || 1,
      }))
    : [{ service_type: normalizeServiceType(wo.service_type) || "OTHER",
         quantity: wo.quantity || 1 }];
  // scheduled_start "YYYY-MM-DDTHH:MM:SS+TZ" → date "YYYY-MM-DD" + time "HH:MM"
  const s = (wo.scheduled_start || "");
  const preferred_date = s.slice(0, 10) || todayWita();
  const preferred_time = s.slice(11, 16) || "09:00";
  // v84.55 — Coba tarik maps_link dari first-class field `wo.maps_link`,
  // fallback ke description parsing utk WO lama.
  let maps_link = wo.maps_link || wo.map_url || "";
  const _mapsLineRe = /^\s*(?:Maps\s*:|Lokasi\s*Pin\s*:).*$/gim;
  if (!maps_link && wo.description) {
    const m = wo.description.match(/(?:Maps\s*:|Lokasi\s*Pin\s*:)\s*(https?:\/\/\S+)/i);
    if (m) maps_link = m[1];
  }
  // v84.55 — Strip baris "Maps:/Lokasi Pin:" dari description form supaya tidak
  // duplicate saat re-save (maps_link akan di-append kembali di saveOrder).
  const cleanDescription = (wo.description || "").replace(_mapsLineRe, "").replace(/\n{3,}/g, "\n\n").trim();
  return {
    items,
    custom_service: "",
    remarks: "",
    address: wo.address || "",
    customer_phone: wo.customer_phone || "",
    customer_name: wo.customer_name || "",
    preferred_date, preferred_time,
    maps_link,
    description: cleanDescription,
    technician_id: wo.technician_id || "",
    color: wo.color || null,
    status: wo.status || "SCHEDULED",   // hanya dipakai di mode edit
  };
}

export default function CreateWODialog({
  open,
  onOpenChange,
  techs = [],
  onCreated,
  onSaved,   // v84.40 — dipanggil setelah edit selesai
  onDeleted, // v84.40 — dipanggil setelah delete
  initial = null,   // {day, hour, technician_id} — dipakai kalender saat click slot
  mode = "create",   // v84.40 — "create" | "edit"
  wo = null,   // v84.40 — WO existing utk mode edit
}) {
  const [sherlockOpen, setSherlockOpen] = useState(false);
  const [sherlockInfo, setSherlockInfo] = useState(null);
  const [form, setForm] = useState(() =>
    mode === "edit" && wo ? makeFormFromWO(wo) : makeEmptyForm(initial || {})
  );
  const [saving, setSaving] = useState(false);
  const [customCats, setCustomCats] = useState([]);
  const [newCatInput, setNewCatInput] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  useMemo(() => {
    if (open) {
      setForm(mode === "edit" && wo
        ? makeFormFromWO(wo)
        : makeEmptyForm(initial || {}));
      setLookupResult(null);
      setNewCatInput("");
      setSherlockInfo(null);
    }
  }, [open, initial, mode, wo]);   // eslint-disable-line react-hooks/exhaustive-deps

  // v84.34 — Load kategori custom dari backend (biar admin tidak ketik ulang)
  useMemo(() => {
    if (!open) return;
    api.listCategories()
      .then(r => setCustomCats((r?.items || []).map(i => i.key)))
      .catch(() => {});
  }, [open]);

  // Gabungan built-in + custom (dedup)
  const allServices = useMemo(() => {
    const set = new Set(SERVICES);
    customCats.forEach(c => set.add(c));
    return Array.from(set);
  }, [customCats]);

  async function doCustomerLookup(phone) {
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length < 8) { setLookupResult(null); return; }
    setLookupBusy(true);
    try {
      const r = await api.customerLookup(phone);
      setLookupResult(r);
      if (r.count === 0) return;
      setForm(prev => {
        const upd = { ...prev };
        if (!upd.customer_name && r.name) upd.customer_name = r.name;
        if (r.count === 1) {
          if (!upd.address) upd.address = r.addresses[0].address;
          if (!upd.maps_link && r.addresses[0].map_link) upd.maps_link = r.addresses[0].map_link;
        }
        return upd;
      });
      if (r.count === 1) toast.success(`Customer ditemukan: ${r.name || "-"}. Alamat auto-fill.`);
      else if (r.count > 1) toast.warning(`Customer punya ${r.count} alamat — pilih salah satu.`);
    } catch (e) {
      if (e?.response?.status !== 400 && e?.response?.status !== 404) console.warn("lookup", e);
    } finally { setLookupBusy(false); }
  }

  function pickAddress(a) {
    setForm(prev => ({
      ...prev,
      address: a.address,
      maps_link: a.map_link || prev.maps_link || "",
    }));
    setLookupResult(null);
    toast.success("Alamat dipilih");
  }

  function updateItem(idx, patch) {
    setForm(prev => {
      const items = [...(prev.items || [])];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  }
  function addItem() {
    setForm(prev => ({
      ...prev,
      items: [...(prev.items || []), { service_type: "Service", quantity: 1 }],
    }));
  }
  function removeItem(idx) {
    setForm(prev => {
      const items = (prev.items || []).filter((_, i) => i !== idx);
      return { ...prev, items: items.length ? items : [{ service_type: "Service", quantity: 1 }] };
    });
  }

  async function deleteCustomCat(catName) {
    if (SERVICES.includes(catName)) {
      toast.error("Kategori bawaan tidak bisa dihapus");
      return;
    }
    if (!window.confirm(`Hapus kategori "${catName}" dari template dropdown?`)) return;
    try {
      await api.deleteCategory(catName);
      const r = await api.listCategories();
      setCustomCats((r?.items || []).map(i => i.key));
      toast.success(`Kategori "${catName}" dihapus`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal hapus kategori");
    }
  }

  async function addCustomCat(idx) {
    const catName = newCatInput.trim();
    if (!catName) return;
    try {
      await api.addCategory(catName);
      const r = await api.listCategories();
      const newList = (r?.items || []).map(i => i.key);
      setCustomCats(newList);
      setNewCatInput("");
      if (typeof idx === "number") updateItem(idx, { service_type: catName });
      toast.success(`Kategori "${catName}" tersimpan sebagai template`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal tambah kategori");
    }
  }

  const saveOrder = useCallback(async () => {
    if (!form.address.trim()) { toast.error("Alamat wajib"); return; }
    const items = (form.items || [])
      .map(it => ({
        service_type: it.service_type === SERVICE_ADD_KEY
          ? (form.custom_service || "OTHER") : (it.service_type || "OTHER"),
        quantity: Math.max(1, Number(it.quantity) || 1),
      }))
      .filter(it => it.service_type);
    if (!items.length) { toast.error("Minimal 1 layanan"); return; }
    const scheduled_start = form.preferred_time
      ? `${form.preferred_date}T${form.preferred_time}:00+08:00` : undefined;
    // v84.55 — Bersihkan `form.description` dari baris "Maps:/Lokasi Pin:" supaya
    // tidak double dengan `maps_link` field (yg akan dikirim terpisah ke backend).
    const cleanDesc = (form.description || "").replace(/^\s*(?:Maps\s*:|Lokasi\s*Pin\s*:).*$/gim, "").replace(/\n{3,}/g, "\n\n").trim();
    const parts = [];
    if (form.remarks) parts.push(`Ket: ${form.remarks}`);
    if (cleanDesc) parts.push(cleanDesc);
    // Note: maps_link tidak lagi diappend ke description text — dikirim sbg field terpisah.
    setSaving(true);
    try {
      if (mode === "edit" && wo) {
        // v84.40 — UPDATE existing WO
        const payload = {
          customer_name: form.customer_name || form.customer_phone || "Customer",
          customer_phone: form.customer_phone,
          address: form.address,
          items,
          service_type: items[0].service_type,
          quantity: items.reduce((s, it) => s + it.quantity, 0),
          scheduled_start,
          description: parts.join("\n"),
          maps_link: form.maps_link || null,      // v84.55 first-class field
          technician_id: form.technician_id || null,
          status: form.status,
          ...(form.color ? { color: form.color } : {}),
        };
        await api.updateOrder(wo.id, payload);
        dispatchWOChanged({ wo: { ...wo, ...payload, id: wo.id }, source: "edit-wo-dialog" });
        // v84.47 — Pull-sync disabled (one-way). Removed api.googlePullSync().
        toast.success("Work order diperbarui");
        onOpenChange(false);
        onSaved?.();
      } else {
        // CREATE new WO
        // v84.135 — Auto Assign: kalau technician_id kosong, JANGAN kirim
        // scheduled_start. Backend scheduler V2 pilih jam + tim otomatis
        // berdasarkan preferred_date. Kalau admin pilih tim spesifik →
        // manual mode, kirim jam yg admin set di form.
        const isManual = !!form.technician_id;
        const payload = {
          customer_name: form.customer_name || form.customer_phone || "Customer",
          customer_phone: form.customer_phone,
          address: form.address,
          items,
          service_type: items[0].service_type,
          quantity: items.reduce((s, it) => s + it.quantity, 0),
          preferred_date: form.preferred_date,
          description: parts.join("\n"),
          maps_link: form.maps_link || null,
          ...(form.color ? { color: form.color } : {}),
        };
        if (isManual) {
          payload.scheduled_start = scheduled_start;
          payload.technician_id = form.technician_id;
        }
        const created = await api.createOrder(payload);
        dispatchWOChanged({ wo: created, source: "create-wo-dialog" });
        // v84.47 — Pull-sync disabled (one-way). Removed api.googlePullSync().
        toast.success(isManual
          ? "Work order dibuat & di-assign manual"
          : "Work order dibuat & auto-assigned (scheduler V2 pilih jam+tim)");
        onOpenChange(false);
        onCreated?.();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`${mode === "edit" ? "Gagal simpan" : "Gagal buat WO"}: ${detail}`);
    } finally { setSaving(false); }
  }, [form, mode, wo, onOpenChange, onCreated, onSaved]);

  async function reassignAI() {
    if (mode !== "edit" || !wo) return;
    try {
      const r = await api.reassign(wo.id);
      if (r.ok) {
        dispatchWOChanged({ source: "reassign", wo: { id: wo.id } });
        toast.success("AI reassign berhasil");
        onOpenChange(false);
        onSaved?.();
      } else toast.error(r.error || "Gagal reassign");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reassign"); }
  }

  async function removeWO() {
    if (mode !== "edit" || !wo) return;
    if (!window.confirm("Hapus work order ini? Event di Google Calendar juga akan dihapus.")) return;
    try {
      await api.deleteOrder(wo.id);
      dispatchWOChanged({ source: "delete", wo: { id: wo.id } });
      toast.success("Work order dihapus");
      onOpenChange(false);
      onDeleted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  }

  const totalQty = (form.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const hasCustomChoice = (form.items || []).some(it => it.service_type === SERVICE_ADD_KEY);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl" data-testid="create-wo-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]" data-testid="create-wo-dialog-title">
            {mode === "edit" ? "Edit Work Order" : "Buat Work Order Manual"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Multi-item layanan */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Layanan {(form.items?.length || 0) > 1 && (
                  <span className="text-orange-500 normal-case ml-1">
                    · {form.items.length} item · total {totalQty} unit
                  </span>
                )}
              </label>
              <button type="button" onClick={addItem}
                className="text-[10px] text-orange-600 hover:text-orange-700 font-medium"
                data-testid="wo-add-item-btn">
                + Tambah Layanan
              </button>
            </div>
            <div className="space-y-1.5" data-testid="wo-items-list">
              {(form.items || []).map((it, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_88px_28px] gap-1.5 items-center"
                  data-testid={`wo-item-${idx}`}>
                  <Select value={it.service_type}
                    onValueChange={v => updateItem(idx, { service_type: v })}>
                    <SelectTrigger className="rounded-sm h-9 text-[12px]"
                      data-testid={`wo-item-${idx}-service`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* v84.148 — Fallback: kalau service_type WO tidak match
                          SERVICES/customCats (mis. "OTHER", "AC_CLEANING",
                          "MANUAL"), render sbg SelectItem sementara supaya
                          Select tetap menampilkan value & tidak kosong. */}
                      {it.service_type
                        && !SERVICES.includes(it.service_type)
                        && !customCats.includes(it.service_type)
                        && it.service_type !== SERVICE_ADD_KEY && (
                          <SelectItem value={it.service_type}
                            data-testid={`wo-item-${idx}-service-legacy`}>
                            {svcLabel(it.service_type)}
                          </SelectItem>
                        )}
                      {/* Bawaan (tidak bisa dihapus) */}
                      {SERVICES.map(s => <SelectItem key={s} value={s}>{svcLabel(s)}</SelectItem>)}
                      {/* Custom (user-added, bisa dihapus) */}
                      {customCats.filter(c => !SERVICES.includes(c)).length > 0 && (
                        <div className="border-t border-border my-1" />
                      )}
                      {customCats.filter(c => !SERVICES.includes(c)).map(s => (
                        <div key={s} className="flex items-center pr-1 hover:bg-white/5">
                          <div className="flex-1"><SelectItem value={s}>{svcLabel(s)}</SelectItem></div>
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteCustomCat(s); }}
                            className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/15 rounded"
                            title={`Hapus kategori "${s}"`}
                            data-testid={`del-cat-${s}`}>
                            🗑
                          </button>
                        </div>
                      ))}
                      <SelectItem value={SERVICE_ADD_KEY}>+ Ketik custom…</SelectItem>
                      <div className="border-t border-border mt-1 pt-1 px-2">
                        <div className="text-[9px] text-zinc-500 mb-1">
                          💡 Simpan sebagai template dropdown:
                        </div>
                        <div className="flex gap-1">
                          <input value={newCatInput} onChange={e => setNewCatInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomCat(idx); } }}
                            placeholder="Contoh: PASANG_AC"
                            className="text-[11px] border rounded-sm px-2 py-1 flex-1 outline-none focus:border-[#E65100]"
                            data-testid={`wo-item-${idx}-new-cat-input`} />
                          <button type="button" onClick={() => addCustomCat(idx)}
                            className="text-[11px] bg-[#E65100] text-white px-2 rounded-sm hover:bg-[#F57C00]"
                            data-testid={`wo-item-${idx}-new-cat-btn`}>+</button>
                        </div>
                      </div>
                    </SelectContent>
                  </Select>
                  <Select value={String(it.quantity)}
                    onValueChange={v => updateItem(idx, { quantity: Number(v) })}>
                    <SelectTrigger className="rounded-sm h-9 text-[12px]"
                      data-testid={`wo-item-${idx}-qty`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10].map(n =>
                        <SelectItem key={n} value={String(n)}>{n} unit</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button type="button" onClick={() => removeItem(idx)}
                    disabled={(form.items || []).length <= 1}
                    className="h-9 rounded-sm border border-zinc-200 text-zinc-500 hover:text-red-600 hover:border-red-300 disabled:opacity-30 disabled:cursor-not-allowed grid place-items-center"
                    data-testid={`wo-item-${idx}-remove`}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            {hasCustomChoice && (
              <Input value={form.custom_service}
                onChange={e => setForm(f => ({ ...f, custom_service: e.target.value }))}
                placeholder="Ketik jenis layanan custom (sekali pakai)…"
                className="rounded-sm h-8 text-[12px] mt-2"
                data-testid="wo-custom-service-input" />
            )}
            <div className="text-[9px] text-zinc-400 mt-1">
              Durasi event dihitung otomatis dari estimator (mis. 1 unit ≈ 45 mnt).
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Keterangan</label>
            <Input value={form.remarks}
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              placeholder="mis. minta jam 10"
              className="rounded-sm h-9 text-[12px]"
              data-testid="wo-remarks-input" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Alamat *</label>
            <Input value={form.address}
              onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              placeholder="jl. trikora no 12 banjarbaru"
              className="rounded-sm h-9 text-[12px]"
              data-testid="wo-address-input" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">
              Nomer HP {lookupBusy && <span className="text-blue-500 normal-case ml-1">↻ mencari…</span>}
            </label>
            <Input value={form.customer_phone}
              onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
              onBlur={e => doCustomerLookup(e.target.value)}
              placeholder="08xxxxxxxxxx"
              className="rounded-sm h-9 text-[12px] font-mono"
              data-testid="wo-phone-input" />
          </div>

          {lookupResult && lookupResult.count > 1 && (
            <div className="p-2.5 rounded-sm bg-amber-50 border border-amber-300"
              data-testid="customer-address-picker">
              <div className="text-[11px] font-semibold text-amber-900 mb-1.5">
                ⚠️ Customer <b>{lookupResult.name || "-"}</b> punya {lookupResult.count} alamat — pilih yg sesuai:
              </div>
              <div className="space-y-1">
                {lookupResult.addresses.map((a, i) => (
                  <button key={i} type="button"
                    onClick={() => pickAddress(a)}
                    className={`w-full text-left p-2 rounded-sm text-[11px] bg-white border transition hover:border-orange-400 hover:bg-orange-50 ${form.address === a.address ? 'border-orange-500 bg-orange-50' : 'border-zinc-200'}`}
                    data-testid={`pick-address-${i}`}>
                    <div className="font-medium text-zinc-800">{a.address}</div>
                    <div className="text-[9px] text-zinc-500 mt-0.5">
                      {a.source === "wo_active" ? "🟢 WO aktif" : a.source === "wo_recent" ? "🕒 WO recent" : "📁 History"}
                      {a.last_used && ` · terakhir ${a.last_used}`}
                      {a.map_link && <span className="ml-1 text-blue-500">· map link ✓</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Tanggal</label>
              <Input type="date" value={form.preferred_date}
                onChange={e => setForm(f => ({ ...f, preferred_date: e.target.value }))}
                className="rounded-sm h-9 text-[12px] font-mono"
                data-testid="wo-date-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">
                Jam {mode !== "edit" && !form.technician_id && <span className="text-[9px] font-normal text-orange-400">(dipilih otomatis)</span>}
              </label>
              <Input type="time" value={form.preferred_time}
                onChange={e => setForm(f => ({ ...f, preferred_time: e.target.value }))}
                disabled={mode !== "edit" && !form.technician_id}
                className={`rounded-sm h-9 text-[12px] font-mono ${mode !== "edit" && !form.technician_id ? "opacity-50" : ""}`}
                data-testid="wo-time-input" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block">Maps Link</label>
              {mode === "edit" && wo?.id && (
                <button type="button" onClick={() => setSherlockOpen(true)}
                  className="text-[11px] text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 font-medium"
                  data-testid="wo-sherlock-manual-btn">
                  <MapPinned className="w-3.5 h-3.5" />Koreksi Sherlock Manual
                </button>
              )}
            </div>
            <Input value={form.maps_link}
              onChange={e => setForm(f => ({ ...f, maps_link: e.target.value }))}
              placeholder="https://maps.app.goo.gl/..."
              className="rounded-sm h-9 text-[12px] font-mono"
              data-testid="wo-maps-input" />
            {mode === "edit" && wo?.id && (
              <SherlockManualDialog open={sherlockOpen} onOpenChange={setSherlockOpen} woId={wo.id}
                onSaved={(u) => { setForm(f => ({ ...f, maps_link: u.maps_link || f.maps_link })); setSherlockInfo(u); dispatchWOChanged({ wo: u, source: "sherlock-manual" }); }} />
            )}
            {mode === "edit" && (sherlockInfo || wo)?.sherlock_corrected && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200"
                data-testid="wo-d-sherlock-corrected">
                {(sherlockInfo || wo).sherlock_correction_source === "manual_admin" ? "Sherlock Dikoreksi Manual" : "Sherlock Dikoreksi GPS"}
                {(sherlockInfo || wo).sherlock_corrected_at && <span className="opacity-80">· {new Date((sherlockInfo || wo).sherlock_corrected_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</span>}
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Deskripsi</label>
            <Textarea value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="mis. bawa tangga tinggi" rows={2}
              className="rounded-sm text-[12px] resize-none"
              data-testid="wo-description-input" />
          </div>

          {/* v84.34 — Color picker event */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5 mb-1.5">
              <Palette className="w-3 h-3" />Warna Event
            </label>
            <div className="flex flex-wrap gap-1.5">
              {GOOGLE_CAL_COLORS.map(c => (
                <button key={c.hex} type="button"
                  onClick={() => setForm(f => ({ ...f, color: c.hex }))}
                  className={`w-6 h-6 rounded-sm border-2 transition ${form.color === c.hex ? "border-zinc-900 scale-110" : "border-transparent hover:border-zinc-400"}`}
                  style={{ background: c.hex }}
                  title={c.name}
                  data-testid={`wo-color-${c.name.toLowerCase()}`} />
              ))}
              <button type="button"
                onClick={() => setForm(f => ({ ...f, color: null }))}
                className={`w-6 h-6 rounded-sm border-2 grid place-items-center text-[10px] transition ${!form.color ? "border-zinc-900 bg-zinc-50" : "border-transparent bg-zinc-100 hover:border-zinc-400"}`}
                title="Otomatis (warna teknisi)"
                data-testid="wo-color-auto">
                <X className="w-3 h-3 text-zinc-500" />
              </button>
            </div>
            {form.color && (
              <div className="text-[9px] text-zinc-500 mt-1 font-mono">
                {form.color} · {GOOGLE_CAL_COLORS.find(c => c.hex === form.color)?.name}
              </div>
            )}
          </div>

          {/* v84.40 — Edit-only: Status select + actual duration feedback */}
          {mode === "edit" && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Status</label>
                <Select value={form.status || "SCHEDULED"}
                  onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="wo-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["NEW","SCHEDULED","ASSIGNED","IN_PROGRESS","DONE","CANCELLED"].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <WOTechEvidence wo={wo} />
            </>
          )}

          <div className="grid grid-cols-12 gap-2 items-end pt-1">
            <div className={mode === "edit" ? "col-span-6" : "col-span-8"}>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1">
                {mode === "edit" ? "Teknisi" : "Assign Manual To (Opsional)"}
              </label>
              <Select value={form.technician_id || "auto"}
                onValueChange={v => setForm(f => ({ ...f, technician_id: v === "auto" ? "" : v }))}>
                <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="wo-assign-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">— Auto Assign —</SelectItem>
                  {(techs || []).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {mode === "edit" ? (
              <>
                <Button onClick={reassignAI} variant="outline"
                  className="col-span-3 h-9 rounded-sm text-[11px]"
                  data-testid="wo-reassign-btn">
                  AI Reassign
                </Button>
                <Button onClick={saveOrder} disabled={saving}
                  className="col-span-3 h-9 bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5"
                  data-testid="wo-submit-btn">
                  <Wand2 className="w-3.5 h-3.5" />
                  {saving ? "Simpan…" : "Simpan"}
                </Button>
              </>
            ) : (
              <Button onClick={saveOrder} disabled={saving}
                className="col-span-4 h-9 bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5"
                data-testid="wo-submit-btn">
                <Wand2 className="w-3.5 h-3.5" />
                {saving ? "Menyimpan…" : (form.technician_id ? "Buat WO" : "Auto Assign")}
              </Button>
            )}
          </div>

          {mode === "edit" && (
            <div className="border-t border-border pt-2">
              <Button onClick={removeWO} variant="ghost"
                className="text-red-500 hover:text-red-400 hover:bg-red-500/10 text-[11px] h-8 rounded-sm"
                data-testid="wo-delete-btn">
                🗑 Hapus Work Order
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
